import React, { useMemo, useCallback } from 'react';
import SideMenu from '../SideMenu/SideMenu';
import useIsMobile from '../../Hooks/useIsMobile';
import uniqueId from 'lodash-es/uniqueId';
import { translate } from '../../Services/translation';
import ProductCard from '../ProductCards/ProductCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import Product from '../../Types/Product';
import clsx from 'clsx';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';

interface ProductsModalProps {
  open: boolean;
  onClosed: () => void;
  products: Product[];
  className?: string;
}

const ProductsModal = ({
  open,
  onClosed,
  products,
  className,
}: ProductsModalProps) => {
  const uuid = useMemo(() => uniqueId(), []);
  const isMobile = useIsMobile();
  const isStoreScreen = useIsStoreScreen();
  const sideMenuContent = useCallback(() => {
    return (
      <div className="overflow-y-auto pb-4 scrollbar-none">
        <Swiper
          slidesPerView={2.3}
          slidesPerGroup={2}
          className="h-full store-screen:block lg:hidden [&_.swiper-wrapper]:h-full [&_.swiper-wrapper]:gap-x-4"
          slidesOffsetBefore={16}
          slidesOffsetAfter={80}
          threshold={5}
        >
          {products.map((product, idx) => (
            <SwiperSlide
              key={product.id + 'slide' + idx + uuid}
              className="h-full"
            >
              <ProductCard
                product={product}
                lazyLoadImage={true}
                className="h-full"
                showPatternImage={true}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="hidden grid-cols-2 gap-4 pl-4 pr-4 store-screen:hidden lg:grid">
          {products.map((product, idx) => (
            <ProductCard
              key={`${product.id}${idx}${uuid}`}
              product={product}
              lazyLoadImage={true}
              className="h-full"
              showPatternImage={true}
            />
          ))}
        </div>
      </div>
    );
  }, [products, uuid]);

  return (
    <SideMenu
      id={`products-modal-${uuid}`}
      from={isMobile || isStoreScreen ? 'bottom' : 'right'}
      open={open}
      onClosed={onClosed}
      title={translate('general.products')}
      className={clsx(
        className,
        'store-screen:h-[unset] store-screen:!rounded-b-none store-screen:rounded-t lg:h-[calc(100%_-_1rem)] lg:rounded md-down:!rounded-b-none md-down:rounded-t'
      )}
      components={{ Content: sideMenuContent }}
      zIndexClass="z-50"
    ></SideMenu>
  );
};

export default ProductsModal;
